import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { RootState } from "store/rootReducer";
import { selectTxnId, useMapStore } from "stores/map";
import { arrayOrEmpty } from "utils";

import { EntityKind } from "../../../models/entityKind";
import { WellInfoT, useWellList } from "../context";
import { IWellGroupMap } from "../context/WellListContext";
import {
  resetWellPages,
  updateTotalCount,
  updateWellGroupMap,
  updateWellList
} from "../context/reducer";
import useGetGroupedEntities from "./useGetGroupedEntities";
import useGetWellsFromFilterId from "./useGetWellsFromFilterId";

export default function useWellsFromFilter() {
  const [{ entityKind }, dispatch] = useWellList();
  const filterId = useSelector((state: RootState) =>
    entityKind == EntityKind.Well ? state.filter.filterId : state.facilityFilter.filterId
  );
  const isFiltering = useSelector((state: RootState) =>
    entityKind == EntityKind.Well
      ? state.filter.isFiltering
      : state.facilityFilter.isFiltering
  );
  const mapTxnId = useMapStore(selectTxnId);

  const handleWellList = useCallback(
    (response) => {
      const wells: WellInfoT[] = arrayOrEmpty<{ uniqueId: string; formattedId: string }>(
        response?.data?.uwiList
      ).map((w): WellInfoT => {
        return {
          id: w.uniqueId,
          label: w.formattedId
        };
      });
      updateWellList(dispatch, wells);
      if (response?.data?.totalCount != null) {
        updateTotalCount(dispatch, response.data.totalCount);
      } else {
        // totalCount is a backend contract; a missing value makes the list
        // silently operate on only the first page. Fall back to what we have
        // so the UI still renders, but surface the violation instead of
        // masking it as correct data.
        Sentry.withScope((scope) => {
          scope.setContext("useWellsFromFilter", { loadedWells: wells.length });
          scope.setLevel("warning");
          Sentry.captureMessage("well list response missing totalCount");
        });
        updateTotalCount(dispatch, wells.length);
      }
    },
    [dispatch]
  );

  const handleEntities = useCallback(
    (groups) => {
      // Convert the entities mapping into
      const mapping: IWellGroupMap = {};
      Object.keys(groups).forEach((key) => {
        const group = groups[key];
        Object.keys(group.uwis).forEach((uwi: string) => {
          mapping[uwi] = {
            group: group.group?.title,
            color: group.group?.color
          };
        });
      });
      updateWellGroupMap(dispatch, mapping);
    },
    [dispatch]
  );

  const onWellListError = () => {
    toast.error("Unable to load well list. Please try again.");
  };

  // Determine the txnId based on entity kind
  const entitiesTxnId = mapTxnId
    ? entityKind === EntityKind.Facility
      ? mapTxnId.facilityId
      : mapTxnId.id
    : null;

  // A kind change swaps the query key under this hook, and the list stays as it
  // was until the new kind's answer is delivered. On the paths that never deliver
  // one — a fetch that errors, or a filter id that was never applied, which is
  // the empty placeholder both slices start with — it would stay the other kind's
  // list indefinitely, which is what makes a panel judge a selection against the
  // wrong entities. Empty is the honest answer until the new kind arrives.
  //
  // Declared before useGetWellsFromFilterId on purpose: effects run in hook
  // order, so this clears and that hook's delivery repaints a cached answer in
  // the same commit, with no empty frame in between.
  const deliveredKindRef = useRef(entityKind);
  useEffect(() => {
    if (deliveredKindRef.current === entityKind) return;
    deliveredKindRef.current = entityKind;
    updateWellList(dispatch, []);
    updateTotalCount(dispatch, 0);
    resetWellPages(dispatch);
  }, [entityKind, dispatch]);

  const {
    isLoading: wellListIsLoading,
    isError: wellListIsError,
    error: wellListError
  } = useGetWellsFromFilterId(
    filterId?.id,
    entitiesTxnId,
    handleWellList,
    onWellListError
  );

  const {
    isLoading: entitiesIsLoading,
    isError: entitiesIsError,
    error: entitiesError
  } = useGetGroupedEntities(entitiesTxnId, handleEntities);

  return {
    isLoading: wellListIsLoading || entitiesIsLoading || isFiltering,
    isError: !!wellListIsError || !!entitiesIsError,
    error: wellListError ?? entitiesError
  };
}
